import { PcmStreamPlayer } from './pcmStreamPlayer.ts'
import {
  AudioRendererStalledError,
  boundedAudioOperation,
  waitForAudioForeground,
  watchRendering,
} from './rendererHealth.ts'
import { throwIfSpeechCancelled } from '../../../shared/api/edenaiApi.ts'
import { AUDIO_PLAY_ERROR } from '../../../shared/lib/howlerUtils.ts'

interface RendererOptions {
  signal: AbortSignal
  getContext: () => AudioContext
  replaceContext: (context: AudioContext) => AudioContext
  resume: (context: AudioContext) => Promise<void>
  paused: () => boolean
  progress: (value: number) => void
  started: () => void
}

/** Recovery never retries synthesis or discards queued speech to restart it. */
export class TeacherAudioRenderer {
  context?: AudioContext
  player?: PcmStreamPlayer
  needsFreshContext = false
  private stopWatching?: () => void
  private resetting = false
  private playbackRecoveryAttempted = false
  private rejectFailure!: (error: unknown) => void
  private readonly failure = new Promise<void>((_resolve, reject) => {
    this.rejectFailure = reject
  })

  constructor(private options: RendererOptions) {}

  get preparedPlayer() {
    if (!this.player) throw new Error('Audio renderer is not prepared')
    return this.player
  }

  get finished() {
    return Promise.race([this.preparedPlayer.finished, this.failure])
  }

  async prepare() {
    this.context = this.options.getContext()
    try {
      await this.connect()
      await this.verifyRendering()
    } catch (error) {
      if (this.options.signal.aborted) throw error

      if (!(error instanceof AudioRendererStalledError)) {
        this.needsFreshContext =
          error instanceof Error && error.message === AUDIO_PLAY_ERROR
        throw error
      }
      await this.recoverStartup()
    }
    throwIfSpeechCancelled(this.options.signal)
    this.stopWatching = watchRendering(
      () => this.renderingState(),
      this.options.paused,
      () => this.recoverPlayback(),
      (error) => {
        this.needsFreshContext = true

        this.rejectFailure(error)
        this.player?.stop()
        this.stopWatching?.()
      },
    )
  }

  private renderingState() {
    return {
      clockTime: this.context?.currentTime ?? 0,
      heartbeats: this.player?.heartbeatCount ?? 0,
    }
  }

  private async connect() {
    const context = this.context
    if (!context) throw new Error('Missing audio context')
    await waitForAudioForeground(this.options.signal, this.options.paused)

    await this.options.resume(context)

    await PcmStreamPlayer.prepare(context, this.options.signal)
    throwIfSpeechCancelled(this.options.signal)

    const player = new PcmStreamPlayer(
      context,
      this.options.progress,
      this.options.started,
    )
    this.player = player
  }

  private async verifyRendering() {
    if (!this.player) throw new Error('Missing audio processor')
    await this.player.waitForRendering(this.options.signal, this.options.paused)
    throwIfSpeechCancelled(this.options.signal)
  }

  private async reset() {
    const context = this.context
    if (!context) throw new Error('Missing audio context')
    this.resetting = true
    try {
      await boundedAudioOperation(
        context.suspend(),
        this.options.signal,
        'AudioContext.suspend()',
      )
      throwIfSpeechCancelled(this.options.signal)
      await waitForAudioForeground(this.options.signal, this.options.paused)
      await this.options.resume(context)
      await this.verifyRendering()
    } finally {
      if (this.options.signal.aborted) this.needsFreshContext = true
      this.resetting = false
    }
  }

  private async recoverStartup() {
    try {
      await this.reset()
    } catch (error) {
      throwIfSpeechCancelled(this.options.signal)

      this.player?.stop()
      const context = this.context
      if (!context) throw error

      this.context = this.options.replaceContext(context)
      this.needsFreshContext = true
      await this.connect()
      await this.verifyRendering()
      this.needsFreshContext = false
    }
  }

  private async recoverPlayback() {
    if (this.playbackRecoveryAttempted) {
      throw new AudioRendererStalledError(
        'Audio renderer stalled again after recovery',
        this.renderingState(),
      )
    }
    this.playbackRecoveryAttempted = true
    // Reset the existing graph to preserve its queued samples and playback position.
    // If this fails, end with a retryable error; never replay an already spoken reply.
    await this.reset()
  }

  stop() {
    if (this.resetting) this.needsFreshContext = true
    this.stopWatching?.()
    this.player?.stop()
  }
}
